/**
 * Tiny anchored dropdown that surfaces the export actions
 * (Download + optional Share) for the currently-open media item.
 *
 * Why this file pre-fetches bytes for sharing:
 *   `navigator.share()` only succeeds while the browser still holds the
 *   "transient user activation" from the click. If we awaited the bytes
 *   *inside* the click handler, Chrome on macOS rejects the call with
 *   `NotAllowedError: Permission denied`. So as soon as the menu opens
 *   we eagerly load the bytes into a cached `File`, and the click
 *   handler can call `shareFile(file)` synchronously.
 */

import { Download, Loader2, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  downloadTarget,
  type ExportTarget,
  fileForTarget,
  isShareAvailable,
  shareFile,
} from "./export.js";

interface ExportMenuProps {
  target: ExportTarget;
  trigger: (props: {
    onClick: () => void;
    "aria-expanded": boolean;
    "aria-haspopup": "menu";
  }) => React.ReactNode;
  onError?: (message: string) => void;
}

export function ExportMenu({ target, trigger, onError }: ExportMenuProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<null | "download" | "share">(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const shareAvailable = useShareAvailable();

  // Pre-fetched File for sharing — populated as soon as the menu opens
  // so the click handler can call `navigator.share` synchronously and
  // keep the browser's transient user activation alive.
  const [shareReady, setShareReady] = useState(false);
  const shareFileRef = useRef<File | null>(null);
  const sharePrepErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Pre-fetch share bytes when the menu opens. Download path doesn't
  // need this — it can safely await inside the click handler.
  useEffect(() => {
    if (!open || !shareAvailable) return;
    let cancelled = false;
    setShareReady(false);
    shareFileRef.current = null;
    sharePrepErrorRef.current = null;
    void (async () => {
      try {
        const file = await fileForTarget(target);
        if (cancelled) return;
        shareFileRef.current = file;
        setShareReady(true);
      } catch (e) {
        if (cancelled) return;
        console.error("[export] share prefetch failed:", e);
        sharePrepErrorRef.current = friendlyExportError(e, "share");
        onError?.(sharePrepErrorRef.current);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, shareAvailable, target, onError]);

  function handleDownload() {
    if (busy) return;
    setBusy("download");
    void (async () => {
      try {
        await downloadTarget(target);
        setOpen(false);
      } catch (e) {
        console.error("[export] download failed:", e);
        onError?.(friendlyExportError(e, "download"));
      } finally {
        setBusy(null);
      }
    })();
  }

  /**
   * Stays synchronous up to the `navigator.share` call — we MUST NOT
   * await anything between the click and the share() invocation, or the
   * browser drops the user activation and rejects with NotAllowedError.
   */
  function handleShare() {
    if (busy) return;
    const file = shareFileRef.current;
    if (!file) {
      if (sharePrepErrorRef.current) onError?.(sharePrepErrorRef.current);
      return;
    }
    setBusy("share");
    shareFile(file)
      .then(() => setOpen(false))
      .catch((e: unknown) => {
        const aborted = e instanceof Error && e.name === "AbortError";
        if (aborted) return;
        console.error("[export] share failed:", e);
        onError?.(friendlyExportError(e, "share"));
      })
      .finally(() => setBusy(null));
  }

  const sharePending = open && shareAvailable && !shareReady;

  return (
    <div ref={wrapperRef} className="relative">
      {trigger({
        onClick: () => setOpen((v) => !v),
        "aria-expanded": open,
        "aria-haspopup": "menu",
      })}
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 min-w-50 overflow-hidden rounded-2xl border border-white/10 p-1 shadow-2xl"
          style={{
            background: "rgba(20,24,18,0.95)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        >
          <MenuItem
            label="Herunterladen"
            icon={<Download className="size-4" />}
            busy={busy === "download"}
            disabled={busy !== null}
            onClick={handleDownload}
          />
          {shareAvailable && (
            <MenuItem
              label={sharePending ? "Teilen (lädt …)" : "Teilen"}
              icon={<Share2 className="size-4" />}
              busy={busy === "share" || sharePending}
              disabled={busy !== null || sharePending}
              onClick={handleShare}
            />
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  label,
  icon,
  busy,
  disabled,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      style={{ color: "#f4f5f3" }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.background = "rgba(178,255,89,0.10)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span className="grid size-5 place-items-center text-pitch-lime">
        {busy ? <Loader2 className="size-4 animate-spin" /> : icon}
      </span>
      {label}
    </button>
  );
}

function useShareAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    setAvailable(isShareAvailable());
  }, []);
  return available;
}

/**
 * Translates the raw error from `navigator.share` / `fetch` / Tauri-fs
 * into a short German user-facing message. The original error is logged
 * to the console for debugging — we deliberately don't surface
 * `NotAllowedError: Permission denied` etc. to the user.
 */
function friendlyExportError(err: unknown, kind: "share" | "download"): string {
  const name = err instanceof Error ? err.name : "";
  const msg = err instanceof Error ? err.message : String(err);

  if (kind === "share") {
    if (name === "NotAllowedError") {
      return "Teilen wurde vom Browser nicht erlaubt.";
    }
    if (name === "DataError" || /canShare/i.test(msg)) {
      return "Diese Datei kann hier nicht geteilt werden.";
    }
    return "Teilen fehlgeschlagen. Bitte erneut versuchen.";
  }

  // download path
  if (/HTTP\s*\d+/i.test(msg)) {
    return "Datei konnte nicht geladen werden.";
  }
  return "Download fehlgeschlagen. Bitte erneut versuchen.";
}
